import {ActivatedRoute} from '@angular/router';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import {FormsModule, ReactiveFormsModule, UntypedFormControl} from "@angular/forms";
import {MatPaginatorModule} from "@angular/material/paginator";
import {MatSortModule} from "@angular/material/sort";
import {finalize, map, Observable, Subject, take, takeUntil} from "rxjs";
import {fuseAnimations} from "@fuse/animations";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {CommonModule, Location, NgForOf, NgIf, NgClass} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatRippleModule} from '@angular/material/core';
import {MatSelectModule} from '@angular/material/select';
import {MatToolbarModule} from '@angular/material/toolbar';
import {environment} from '../../../../../environments/environment';
import {DestinationType} from '../destinations.types';
import {FuseConfirmationService} from '../../../../../@fuse/services/confirmation';
import {SpecialOffersListComponent} from '../special-offers-list/special-offers-list.component';
import {RestFactoryService} from '../../../../core/services/rest-factory.service';
import {NgxSpinnerComponent} from "ngx-spinner";
import {DestinationClientManagerComponent} from "./destination-client-manager/destination-client-manager.component";
import {HomeTabComponent} from "./home-tab/home-tab.component";
import {FeaturesTabComponent} from "./features-tab/features-tab.component";
import {MapTabComponent} from "./map-tab/map-tab.component";
import {GalleryTabComponent} from "./gallery-tab/gallery-tab.component";
import {StoriesTabComponent} from "./stories-tab/stories-tab.component";
import {ProductsTabComponent} from "./products-tab/products-tab.component";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatListModule} from "@angular/material/list";
import {MatDividerModule} from "@angular/material/divider";
import {MatTooltipModule} from "@angular/material/tooltip";
import {
    DestinationRelationshipManagerComponent
} from "./destination-relationship-manager/destination-relationship-manager.component";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {ChangeHistoryComponent} from "./change-history/change-history.component";
import {CloneDestinationDialogComponent} from "./clone-destination/clone-destination-dialog.component";
import {TranslationSettingsComponent} from "./translation-settings/translation-settings.component";
import {SplashScreenComponent} from "../../../../core/splash-screen/splash-screen.component";
import {DestinationMainMediaComponent} from "./destination-main-media/destination-main-media.component";
import {ProjectComponent} from "../../../dashboards/project/project.component";

@Component({
    selector: "destinations-management",
    templateUrl: "./destinations-management.component.html",
    styleUrls: ["./destinations-management.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        CommonModule,
        NgIf,
        NgForOf,
        NgClass,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatRippleModule,
        MatSortModule,
        MatSelectModule,
        MatToolbarModule,
        MatDialogModule,
        SpecialOffersListComponent,
        NgxSpinnerComponent,
        DestinationClientManagerComponent,
        HomeTabComponent,
        FeaturesTabComponent,
        MapTabComponent,
        GalleryTabComponent,
        StoriesTabComponent,
        ProductsTabComponent,
        MatSidenavModule,
        MatListModule,
        MatDividerModule,
        MatTooltipModule,
        DestinationRelationshipManagerComponent,
        ChangeHistoryComponent,
        TranslationSettingsComponent,
        SplashScreenComponent,
        DestinationMainMediaComponent,
        ProjectComponent,
    ]
})
export class DestinationsManagementComponent implements OnInit, OnDestroy {
    @Output() commandEvent = new EventEmitter<string>();
    @Input() destination_ID: any;
    @Input() adminMode: boolean = false;
    @Input() reviewMode: boolean = false;
    @Input() destinationTypesArray: any[] = [];

    isAffiliatesList: boolean = false;
    isLoading: boolean = true;
    initializationError: boolean = false;
    isHandset: Observable<boolean>;
    isHandsetValue: boolean;
    destination: DestinationType;
    destination_relationships: any[] = [];
    destination_ar_history: any[] = [];
    pendingReviewCount: number = 0;
    commentInputControl: UntypedFormControl = new UntypedFormControl("");
    disableSave: boolean = false;
    cancelingReview: boolean = false;
    isMapTabSelected: boolean = false;
    clientData: any = null;
    managerData: any[] = [];
    selectedView: string = 'home';
    highlightReviewButton: boolean = false;
    userUiType: number = environment.user_ui_type || 1;
    offersValidationStatus: boolean = false;

    private pendingAction: string | null = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private API_OWNER_GET_DESTINATION = "/admin/destination/get";
    private API_POST_DESTINATION = "/admin/destination/set";
    private API_POST_CHANGETYPE = "/destinationowner/changetype";
    private API_DELETE_DESTINATION = "/admin/destination/delete";
    private API_UNPUBLISH_DESTINATION = "/admin/destination/destinationunpublish";
    private API_CHANGE_PARENT = "/admin/destination/setparent";
    private API_SEND_TO_REVIEW = "/admin/destination/sendtoreview";
    private API_REVIEW_GET = "/admin/destination/review/get";
    private API_REVIEW_CANCEL = "/admin/destination/review/cancel";
    private API_REVIEW_REJECT = "/admin/destination/review/reject";
    private API_REVIEW_APPROVE = "/admin/destination/review/approve";
    private API_PUBLISH_GET = "/admin/destination/publish/get";
    private API_PUBLISH_CANCEL = "/admin/destination/publish/cancel";
    private API_PUBLISH_REJECT = "/admin/destination/publish/reject";
    private API_PUBLISH_APPROVE = "/admin/destination/publish/publish";
    private API_GET_OFFERS = "/system/offer/get";

    constructor(
        private _fuseConfirmationService: FuseConfirmationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _restFactoryService: RestFactoryService,
        private _route: ActivatedRoute,
        private _breakpointObserver: BreakpointObserver,
        private _dialog: MatDialog,
        private _location: Location,
    ) {
        this.isHandset = this._breakpointObserver.observe(Breakpoints.Handset)
            .pipe(
                map(result => result.matches)
            );
    }

    ngOnInit(): void {
        this.isHandset
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                this.isHandsetValue = value;
                this._changeDetectorRef.markForCheck();
            });

        this.isAffiliatesList = window.location.pathname.includes('content/destinations/list/affiliates');
        this.initializeDestination();

        this._route.queryParamMap
            .pipe(take(1))
            .subscribe((params) => {
                if (!this.adminMode) {
                    const id = params.get('id_destination');
                    if (id) {
                        this.destination_ID = id;
                    }
                }

                const qaction = params.get('qaction');
                if (qaction) {
                    this.pendingAction = qaction;
                }

                this.dataInitializer();
            });
    }

    ngOnDestroy(): void {
        if (this.reviewMode && !this.cancelingReview) {
            this.cancelingReview = true;
            this.cancelReview();
        }
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isMissingData(val: any, isImage: boolean = false): boolean {
        if (val === null || val === undefined) {
            return true;
        }
        if (typeof val === 'string') {
            const stripped = val.replace(/<[^>]*>?/gm, '').trim();
            if (stripped.length === 0 && !val.includes('<img') && !val.includes('<iframe')) {
                return true;
            }
            if (isImage && val.trim().endsWith('content/undefined')) {
                return true;
            }
        }
        return false;
    }

    isMissingCoordinate(val: any): boolean {
        if (val === null || val === undefined || val === '') {
            return true;
        }
        const num = Number(val);
        return isNaN(num) || num === 0;
    }

    hasHomeErrors(): boolean {
        if (!this.destination) return false;
        return this.isMissingData(this.destination.ds_label) ||
            this.isMissingData(this.destination.id_destinationtype) ||
            !this.destination.parent ||
            this.isMissingData(this.destination.tx_description);
    }

    hasMainMediaErrors(): boolean {
        if (!this.destination) return false;
        return this.isMissingData(this.destination.im_mediacard, true) ||
            this.isMissingData(this.destination.im_media, true);
    }

    hasMapErrors(): boolean {
        if (!this.destination) return false;
        if (this.destination.id_destinationtype === "2a213080-4272-43b5-b0db-6d35bf1c0bec") return false;
        return this.isMissingData(this.destination.ds_googleaddress) ||
            this.isMissingData(this.destination.ds_googlematch) ||
            this.isMissingCoordinate(this.destination.vr_lat) ||
            this.isMissingCoordinate(this.destination.vr_long);
    }

    hasGalleryErrors(): boolean {
        if (!this.destination) return false;
        return !this.destination.ar_gallery || this.destination.ar_gallery.length < 1;
    }

    loadOffersValidation(): void {
        if (!this.destination?.bl_offers) return;

        if (this.destination.ds_destinationtype === 'Ambassador') {
            this.offersValidationStatus = false;
            this._changeDetectorRef.markForCheck();
            return;
        }

        const payload = this._restFactoryService.createPayloadPaginator('', null, { id_destination: this.destination_ID });
        this._restFactoryService.httpPostService(this.API_GET_OFFERS, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(response => {
                const offers = response.ar_results || [];
                this.offersValidationStatus = !this.hasValidActiveOffer(offers);
                this._changeDetectorRef.markForCheck();
            });
    }

    hasValidActiveOffer(offers: any[]): boolean {
        if (!offers || offers.length === 0) return false;
        let hasActive = false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (const offer of offers) {
            if (this.isMissingData(offer.ds_offer) ||
                this.isMissingData(offer.ds_shortname) ||
                this.isMissingData(offer.tx_description) ||
                this.isMissingData(offer.im_media, true)) {
                continue;
            }

            const startVal = offer.dt_start?.datetime?.date || offer.dt_start;
            const endVal = offer.dt_end?.datetime?.date || offer.dt_end;
            const startDate = new Date(startVal);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(endVal);
            endDate.setHours(0, 0, 0, 0);

            if (startDate <= today && today <= endDate) {
                hasActive = true;
                break;
            }
        }
        return hasActive;
    }

    get reviewNavItems() {
        if (!this.destination) return [];
        const items = [];
        if (this.hasHomeErrors()) items.push({id: 'home', label: this.userUiType === 4 ? 'Edit Profile' : 'Home', icon: 'home'});
        if (this.hasMainMediaErrors()) items.push({id: 'main-media', label: 'Main Media', icon: 'image'});
        if (this.hasMapErrors()) items.push({id: 'map', label: 'Map', icon: 'map'});
        if (this.hasGalleryErrors()) items.push({id: 'gallery', label: 'Gallery', icon: 'collections'});
        if (this.destination.bl_offers && this.offersValidationStatus && this.destination.ds_destinationtype !== 'Ambassador') items.push({id: 'offers', label: 'Special Offers', icon: 'local_offer'});
        return items;
    }

    get totalErrorsCount(): number {
        return this.reviewNavItems.length;
    }

    private _handleQuickAction(action: string): void {
        if (!action) return;

        switch (action) {
            case 'blog':
                if (this.destination?.ar_storriesgrouped?.length > 0) {
                    this.selectedView = 'story_' + this.destination.ar_storriesgrouped[0].ds_grouplabel;
                } else {
                    this.selectedView = 'home';
                }
                break;
            case 'ambassadorpicks':
                if (this.destination_relationships?.length > 0) {
                    this.selectedView = 'relationship_' + this.destination_relationships[0].ds_en_dependencytype;
                } else {
                    this.selectedView = 'home';
                }
                break;
            case 'features':
                this.selectedView = 'home';
                break;
            case 'addimages':
                this.selectedView = 'gallery';
                break;
            case 'addoffers':
                this.selectedView = 'offers';
                break;
        }
    }

    sendCommand(command: string): void {
        this.commandEvent.emit(command);
    }

    initializeDestination(): void {
        this.destination = {
            bl_locationboundaries: false, bl_offers: false, ds_targeturl: '', ar_gallery: [], ar_products: [],
            ar_props: [], ar_stories: [], ar_storriesgrouped: [], ds_client: '', ds_countryiso2: '',
            ds_countryiso3: '', ds_countryname: '', ds_destinationtype: '', ds_en_destinationtype: '',
            ds_en_state: '', ds_friendlyurl: '', ds_friendlyurlfull: '', ds_googleaddress: '', ds_googlematch: '',
            ds_iatacitycode: '', ds_label: '', ds_languageietftag: '', ds_languageiso2: '', ds_layoutdestination: '',
            ds_name: '', ds_regioniso3: '', ds_regionname: '', ds_shortname: '', ds_subtitle: '', ds_summary: '',
            ds_title: '', en_destinationtype: 0, en_state: 0, ggparent: null, gparent: null, id_client: '',
            id_country: '', id_destination: '', id_destinationtype: '', id_displaylayout: '', id_language: '',
            id_region: '', im_media: '', im_mediacard: '', im_mediamap: '', im_mediavideo: '', parent: null,
            tx_description: '', vr_lat: 0, vr_long: 0, vr_northeastlong: 0, vr_northeasttlat: 0, vr_northwestlat: 0,
            vr_northwestlong: 0, vr_rating: 0, vr_southeastlat: 0, vr_southeastlong: 0, vr_southwestlat: 0,
            vr_southwestlong: 0, ds_phone: '', ds_email: '', ds_website: '', bl_translationdisabletitle: false,
        };
    }

    dataInitializer(): void {
        this.isLoading = true;
        this.initializationError = false;
        this._changeDetectorRef.markForCheck();

        const payload = new FormData();
        if (this.destination_ID) {
            payload.append("id_destination", this.destination_ID);
        }

        let dynaAPI = this.API_OWNER_GET_DESTINATION;
        if (window.location.pathname.includes('/review')) {
            dynaAPI = this.API_REVIEW_GET;
        } else if (window.location.pathname.includes('/publish')) {
            dynaAPI = this.API_PUBLISH_GET;
        }

        this._restFactoryService.httpPostService(dynaAPI, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (response) => {
                    if (!response || !response.destination) {
                        this.initializationError = true;
                        return;
                    }

                    this.destination = response.destination;
                    this.destination_ID = response.destination.id_destination;
                    this.destination_ar_history = response.ar_editHistory || [];
                    this.pendingReviewCount = this.destination_ar_history.filter(entry => !entry.dt_reviewed).length;
                    this.clientData = response.client;
                    this.managerData = response.ar_managers;
                    this.destination_relationships = response.ar_relationships;
                    this.disableSave = false;

                    this.loadOffersValidation();

                    if (this.pendingAction) {
                        this._handleQuickAction(this.pendingAction);
                        this.pendingAction = null;
                    }
                },
                error: (err) => {
                    this.initializationError = true;
                }
            });
    }

    retryDataInitialization(): void {
        this.dataInitializer();
    }

    goBack(): void {
        this._location.back();
    }

    saveAndClose(navigate: boolean = false, who: string = null): void {
        if (this.disableSave || this.isLoading) return;

        const payload = new FormData();
        payload.append("js_destinationdata", JSON.stringify({destination: this.destination}));
        if (this.destination_ID) {
            payload.append("id_destination", this.destination_ID);
        }

        this._restFactoryService.httpPostService(this.API_POST_DESTINATION, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                if (navigate) {
                    this.onNavigate();
                }

                this.highlightReviewButton = true;
                this._changeDetectorRef.markForCheck();

                setTimeout(() => {
                    this.highlightReviewButton = false;
                    this._changeDetectorRef.markForCheck();
                }, 15000);
            });
    }

    saveAndReload(event: any): void {
        if (!this.destination) return;
        const payload = new FormData();
        payload.append("id_destinationtype", event.value);
        payload.append("id_destination", this.destination.id_destination);

        this._restFactoryService.httpPostService(this.API_POST_CHANGETYPE, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.initializeDestination();
                this.dataInitializer();
            });
    }

    handleParentChange(parentId: string): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);
        payload.append("id_destinationparent", parentId);

        this._restFactoryService.httpPostService(this.API_CHANGE_PARENT, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => this.dataInitializer());
    }

    selectView(viewId: string): void {
        if (viewId === 'map' && !this.isMapTabSelected) {
            this.isMapTabSelected = true;
        }

        if (this.selectedView === 'offers' && viewId !== 'offers') {
            this.loadOffersValidation();
        }

        this.selectedView = viewId;
    }

    onNavigate(): void {
        if (this.destination.ds_destinationtype === 'Ambassador') {
            window.open(environment.previewURL + "/ambassador?bl_preview=1&id=" + this.destination.id_destination, "_blank");
        } else {
            window.open(environment.previewURL + this.destination.ds_targeturl + "&bl_preview=1", "_blank");
        }
    }

    deleteDestination(): void {
        const confirmation = this._fuseConfirmationService.open({
            title: `Delete Destination ${this.destination.ds_name}?`,
            message: `The destination will be deleted permanently. This action cannot be undone.`,
            actions: {confirm: {show: true, label: 'Delete'}, cancel: {show: true}}
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result === "confirmed") {
                const payload = new FormData();
                payload.append("id_destination", this.destination_ID);
                this._restFactoryService.httpPostService(this.API_DELETE_DESTINATION, payload)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.sendCommand("search"));
            }
        });
    }

    togglePublish(): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);
        this._restFactoryService.httpPostService(this.API_UNPUBLISH_DESTINATION, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._fuseConfirmationService.open({
                    title: 'Success',
                    message: 'The destination was successfully unpublished and WILL NOT show up. You can still preview or change things if you want to.',
                    actions: {confirm: {show: true, label: 'OK'}, cancel: {show: false}}
                });
            });
    }

    reviewAction(action: 'approve' | 'reject'): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);
        payload.append("ds_comment", this.commentInputControl.value);

        let dynaAPI = "";
        dynaAPI = action === "approve" ? this.API_REVIEW_APPROVE : this.API_REVIEW_REJECT;

        this._restFactoryService.httpPostService(dynaAPI, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._fuseConfirmationService.open({
                    title: `Destination ${action === "approve" ? "Approved" : "Rejected"}`,
                    message: `The destination ${this.destination.ds_name} has been ${action === "approve" ? "approved" : "rejected"}.`,
                    actions: {confirm: {show: true}, cancel: {show: false}}
                }).afterClosed().subscribe(() => {
                    this.cancelingReview = true;
                    this.sendCommand("search");
                });
            });
    }

    sendToReview(): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);

        this._restFactoryService.httpPostService(this.API_SEND_TO_REVIEW, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._fuseConfirmationService.open({
                    title: "Success",
                    message: `Thank you! Your changes to ${this.destination.ds_name} have been submitted successfully.`,
                    actions: {confirm: {show: true}, cancel: {show: false}}
                }).afterClosed().subscribe(() => {
                    this.cancelingReview = true;
                    this.sendCommand("search");
                });
            });
    }

    cancelReview(): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);

        let dynaAPI = "";
        if (window.location.pathname.includes('/review')) {
            dynaAPI = this.API_REVIEW_CANCEL;
        } else if (window.location.pathname.includes('/publish')) {
            dynaAPI = this.API_PUBLISH_CANCEL;
        }

        if (dynaAPI) {
            this._restFactoryService.httpPostService(dynaAPI, payload)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.cancelingReview = true;
                    this.sendCommand("search");
                });
        }
    }

    publishDestination(): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);
        this._restFactoryService.httpPostService(this.API_PUBLISH_APPROVE, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._fuseConfirmationService.open({
                    title: "Changes Published",
                    message: `The destination ${this.destination.ds_name} changes has been published successfully.`,
                    actions: {confirm: {show: true}, cancel: {show: false}}
                }).afterClosed().subscribe(() => {
                });
            });
    }

    cloneDestination(): void {
        const dialogRef = this._dialog.open(CloneDestinationDialogComponent, {
            width: '800px',
            maxWidth: '95vw',
            data: {
                destination: this.destination,
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.success) {
                this._fuseConfirmationService.open({
                    title: 'Cloning Process Completed',
                    message: `The destination(s) have been successfully cloned. The list will now be refreshed.`,
                    actions: {confirm: {show: true, label: 'Awesome!'}, cancel: {show: false}}
                }).afterClosed().subscribe(() => this.sendCommand('search'));
            }
        });
    }
}
